import { t } from "@lingui/macro";
import { ScrollView } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Avatar } from "@whoosh/design-system/src/components/display";
import {
  KebabVerticalIcon,
  PersonPlusIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { MenuOptionRow } from "./MenuOptionRow";
import type { Attendee } from "./types";

type Props = {
  attendees: Attendee[];
  displayAttendees: Attendee[];
  ownerId: string | null;
  remainingSlots: number | null;
  maxPartySize: number | null;
  isAtCapacity: boolean;
  onAddPress: () => void;
  onReplace: (playerId: string) => void;
  onRemove: (playerId: string) => void;
};

export const AttendeeListContent = ({
  attendees,
  displayAttendees,
  ownerId,
  remainingSlots,
  maxPartySize,
  isAtCapacity,
  onAddPress,
  onReplace,
  onRemove,
}: Props) => (
  <ScrollView style={{ flex: 1 }}>
    <VStack gap={5} paddingVertical={2}>
      <VStack gap={3}>
        {displayAttendees.map((attendee) => {
          const isOwner = attendee.playerId === ownerId;
          const disableRemove = attendees.length <= 1;

          return (
            <HStack
              key={attendee.playerId}
              alignItems="center"
              justifyContent="space-between"
              backgroundColor="surface-primary"
              padding={5}
            >
              <HStack alignItems="center" gap={3}>
                <Avatar
                  size="xs"
                  firstName={attendee.firstName ?? undefined}
                  lastName={attendee.lastName ?? undefined}
                  profileImagePublicId={
                    attendee.profileImagePublicId ?? undefined
                  }
                />
                <Text variant="mainBaseSemiBold">{attendee.displayName}</Text>
              </HStack>
              <HStack alignItems="center" gap={2}>
                {isOwner ? (
                  <Box
                    paddingHorizontal={1}
                    borderWidth={1}
                    borderColor="outline-default"
                    borderRadius="1"
                  >
                    <Text variant="mainMicro">{t`OWNER`}</Text>
                  </Box>
                ) : null}
                {!isOwner ? (
                  <Menu
                    openPosition="bottom-end"
                    trigger={({ onPress }) => (
                      <Button
                        size="tiny"
                        variant="secondary"
                        onPress={onPress}
                        leftIcon={<KebabVerticalIcon />}
                        accessibilityLabel={t`Open attendee actions`}
                      />
                    )}
                    content={({ closeMenu }) => (
                      <Menu.Content>
                        <VStack>
                          <MenuOptionRow
                            label={t`Replace`}
                            onPress={() => {
                              onReplace(attendee.playerId);
                              closeMenu();
                            }}
                          />
                          <MenuOptionRow
                            label={t`Remove`}
                            onPress={() => {
                              onRemove(attendee.playerId);
                              closeMenu();
                            }}
                            disabled={disableRemove}
                          />
                        </VStack>
                      </Menu.Content>
                    )}
                  />
                ) : null}
              </HStack>
            </HStack>
          );
        })}
      </VStack>

      <VStack paddingHorizontal={5}>
        <Button
          variant="outline"
          width="100%"
          accessibilityLabel={t`Add Member Button`}
          testID="add-member-button"
          leftIcon={<PersonPlusIcon />}
          onPress={onAddPress}
          isDisabled={isAtCapacity}
        >
          {t`Add Member`}
        </Button>
      </VStack>
    </VStack>
  </ScrollView>
);
